import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Save, X, AlertTriangle, Loader2 } from "lucide-react";

/**
 * Système global de garde anti-perte de modifications non enregistrées.
 *
 * 3 composants :
 *   - `UnsavedChangesProvider` : à placer en haut de l'App (englobe les routes)
 *   - `useUnsavedChangesGuard(isDirty, onSaveAsync)` : à appeler dans la page éditable
 *   - `useSafeNavigate()` : à utiliser dans la sidebar/header au lieu de navigate()
 *
 * Comportement :
 *   - Clic sur NavLink/lien interne ou bouton Retour avec isDirty=true → dialog 3 choix
 *   - Fermeture onglet/refresh avec isDirty=true → confirmation native beforeunload
 */

const UnsavedChangesContext = createContext({
  isDirty: false,
  setGuardState: () => {},
  safeNavigate: () => {},
});

export function UnsavedChangesProvider({ children }) {
  const [guardState, setGuardState] = useState({ isDirty: false, onSave: null });
  const [pendingNav, setPendingNav] = useState(null);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const safeNavigate = useCallback((to, options) => {
    if (guardState.isDirty) {
      setPendingNav({ to, options });
    } else {
      navigate(to, options);
    }
  }, [guardState.isDirty, navigate]);

  // beforeunload pour fermeture onglet / refresh
  useEffect(() => {
    if (!guardState.isDirty) return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [guardState.isDirty]);

  const proceed = useCallback(() => {
    if (!pendingNav) return;
    const { to, options } = pendingNav;
    setPendingNav(null);
    // Reset guard state pour éviter de re-trigger la dialog sur la navigation
    setGuardState({ isDirty: false, onSave: null });
    navigate(to, options);
  }, [pendingNav, navigate]);

  const handleSave = useCallback(async () => {
    if (!guardState.onSave) {
      proceed();
      return;
    }
    setSaving(true);
    try {
      const ok = await guardState.onSave();
      setSaving(false);
      if (ok === false) return;  // save a échoué — on reste
      proceed();
    } catch (e) {
      setSaving(false);
    }
  }, [guardState.onSave, proceed]);

  const handleDiscard = useCallback(() => {
    proceed();
  }, [proceed]);

  const handleStay = useCallback(() => {
    setPendingNav(null);
  }, []);

  const contextValue = useMemo(
    () => ({ isDirty: guardState.isDirty, setGuardState, safeNavigate }),
    [guardState.isDirty, setGuardState, safeNavigate],
  );

  return (
    <UnsavedChangesContext.Provider value={contextValue}>
      {children}

      <Dialog open={!!pendingNav} onOpenChange={(o) => !o && handleStay()}>
        <DialogContent className="max-w-md" data-testid="unsaved-changes-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <AlertTriangle className="w-6 h-6" />
              Modifications non enregistrées
            </DialogTitle>
            <DialogDescription className="text-base pt-2">
              Vous avez des modifications qui n'ont pas été sauvegardées sur ce document.
              <br />
              Que voulez-vous faire ?
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-3 mt-2">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#52a675] hover:bg-[#3d8a5c] text-white py-6 text-base"
              data-testid="unsaved-save-btn"
            >
              {saving ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Save className="w-5 h-5 mr-2" />}
              {saving ? "Enregistrement..." : "Enregistrer puis quitter"}
            </Button>

            <Button
              onClick={handleDiscard}
              variant="outline"
              disabled={saving}
              className="border-red-300 text-red-700 hover:bg-red-50 py-6 text-base"
              data-testid="unsaved-discard-btn"
            >
              <X className="w-5 h-5 mr-2" />
              Quitter sans enregistrer
            </Button>

            <Button
              onClick={handleStay}
              variant="ghost"
              disabled={saving}
              className="py-4 text-sm"
              data-testid="unsaved-stay-btn"
            >
              Continuer l'édition
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </UnsavedChangesContext.Provider>
  );
}

/**
 * À appeler dans une page éditable.
 * `isDirty` indique si des modifs sont en attente. `onSave` est appelé si l'utilisateur
 * clique "Enregistrer puis quitter" — doit retourner truthy en cas de succès, false en cas d'échec.
 */
export function useUnsavedChangesGuard(isDirty, onSave) {
  const { setGuardState } = useContext(UnsavedChangesContext);
  // Stocke onSave dans un ref pour avoir une référence stable
  // (sinon: re-render → nouvelle fn → setGuardState → re-render → boucle infinie)
  const onSaveRef = useRef(onSave);
  useEffect(() => { onSaveRef.current = onSave; });
  const stableOnSave = useCallback(() => onSaveRef.current?.(), []);

  useEffect(() => {
    setGuardState({ isDirty: !!isDirty, onSave: stableOnSave });
    return () => setGuardState({ isDirty: false, onSave: null });
  }, [isDirty, stableOnSave, setGuardState]);
}

/** Navigation guardée — équivalent de navigate() mais respecte le guard. */
export function useSafeNavigate() {
  const { safeNavigate } = useContext(UnsavedChangesContext);
  return safeNavigate;
}

/** Hook pour lire l'état de garde (utile à AppShell pour intercepter les NavLink). */
export function useGuardState() {
  return useContext(UnsavedChangesContext);
}

/** Bandeau jaune persistant. */
export function UnsavedChangesBanner({ count = null, onSave, onCancel, saving = false }) {
  return (
    <div
      className="sticky top-0 z-30 bg-amber-100 border-b-2 border-amber-300 text-amber-900 px-4 py-3 flex items-center gap-3 shadow-sm"
      data-testid="unsaved-changes-banner"
    >
      <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold">
          {count !== null
            ? `${count} modification${count > 1 ? "s" : ""} non enregistrée${count > 1 ? "s" : ""}`
            : "Modifications non enregistrées"}
        </div>
        <div className="text-xs text-amber-700 hidden sm:block">
          Cliquez sur Enregistrer pour valider, ou Annuler pour revenir à l'état précédent.
        </div>
      </div>
      <Button
        onClick={onCancel}
        variant="outline"
        size="sm"
        disabled={saving}
        className="border-amber-400 text-amber-800 hover:bg-amber-200"
        data-testid="unsaved-banner-cancel"
      >
        <X className="w-3.5 h-3.5 mr-1" /> Annuler
      </Button>
      <Button
        onClick={onSave}
        size="sm"
        disabled={saving}
        className="bg-[#52a675] hover:bg-[#3d8a5c]"
        data-testid="unsaved-banner-save"
      >
        {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
        Enregistrer
      </Button>
    </div>
  );
}

// Compatibilité (anciennes imports) — le composant Dialog est maintenant dans le Provider
export function UnsavedChangesDialog() { return null; }
